// DS5.5, DS5.5.1
import React, { useContext, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
import EmptyWatchedGroups from './_partials/EmptyWatchedGroups';
import NetworkBarLink from './_partials/NetworkBarLink';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import HomeIcon from '@wix/da-ds/pkg/Icons/24x24/Home';
import Eyecon from '@wix/da-ds/pkg/Icons/24x24/FollowingWatch';
import ShopIcon from '@wix/da-ds/pkg/Icons/24x24/Shop';
import DailyDeviationIcon from '@wix/da-ds/pkg/Icons/24x24/DailyDeviation';
import TopicsIcon from '@wix/da-ds/pkg/Icons/24x24/Topics';
import NewestIcon from '@wix/da-ds/pkg/Icons/24x24/Newest';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import RecommendedIcon from '@wix/da-ds/pkg/Icons/Recommended';
import LightningIcon from '@wix/da-ds/pkg/Icons/24x24/Lightning';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import AddToGroupFill from '@wix/da-ds/pkg/Icons/16x16/AddToGroupFill';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import { watchedGroupsStreamId, recommendedGroupsStreamId } from './redux/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useScrollDescendantIntoView } from './hooks/useScrollDescendantIntoView';
import FoldableGroupList from './_partials/FoldableGroupList';
import NetworkBarItemGroup from './_partials/NetworkBarItemGroup';
import SiteHeaderPlacement from '../DealerAd/Placement/SiteHeader';
import { BiData } from '@wix/da-bi/pkg/events';
import s from './NetworkBar.scss';
const menuItemIconMap = {
    home: HomeIcon,
    watch: Eyecon,
    shop: ShopIcon,
    dailyDeviations: DailyDeviationIcon,
    topics: TopicsIcon,
    new: NewestIcon,
    dreamup: DreamUpIcon,
    dailyChallenges: LightningIcon,
    dreamupAnon: DreamUpIcon
};
function useFilteredMainNavigationItems(items) {
    const isMobile = useContext(MobileContext);
    const { isLoggedIn, aiContentFilter } = useContext(SessionContext);
    const features = useEnabledFlagList();
    return items.filter((item)=>{
        if (!item || item.key === 'new') {
            return false;
        }
        if (item.restrictLoggedIn && !isLoggedIn || item.anonOnly && isLoggedIn) {
            return false;
        }
        if (item.desktopOrMobileOnly === 'desktop' && isMobile || item.desktopOrMobileOnly === 'mobile' && !isMobile) {
            return false;
        }
        if (item.hideIfAISuppressed && aiContentFilter === 'suppress') {
            return false;
        }
        if (item.withFeature && !features.includes(item.withFeature) || item.hideWithFeature && features.includes(item.hideWithFeature)) {
            return false;
        }
        return true;
    });
}
const NetworkBar = ({ id, initialFetch, isVisible = true, isCollapsed, selectedItem, showWatchedGroupsEmptyState, showRecommendedGroups, className, isInitialized, mainNavItems, canSeeOldWatch })=>{
    const [hideContent, setHideContent] = useState(true);
    const scrollableRef = useRef(null);
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const { isLoggedIn, hasCoreMembership } = useContext(SessionContext);
    const tabIndex = isVisible ? 0 : -1;
    const mainNavItemsFiltered = useFilteredMainNavigationItems(mainNavItems);
    const createGroupLink = /*#__PURE__*/ React.createElement(NetworkBarLink, {
        label: "groupsCreate",
        url: Url.to(URLS.GROUPS_CREATE),
        iconElement: /*#__PURE__*/ React.createElement("span", {
            className: classnames(s['create-group'], isCollapsed && s['create-group-collapsed'])
        }, /*#__PURE__*/ React.createElement(AddIcon, {
            disableDefaultColors: true,
            size: IconSize.SMALLER
        })),
        tabIndex: tabIndex,
        isCollapsed: !isMobile && isCollapsed,
        showTooltip: true
    });
    useScrollDescendantIntoView(scrollableRef, s['selected-item'], isInitialized);
    useEffect(()=>{
        if (!isInitialized && isVisible) {
            initialFetch();
        }
        setHideContent(false);
    }, [
        initialFetch,
        isInitialized,
        isVisible
    ]);
    return /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            is_expanded: !isCollapsed ? 1 : 0
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(!isMobile && s['scroll-wrapper'], isCollapsed && s['collapsed'], hideContent && s['hidden'], className),
        ref: scrollableRef
    }, /*#__PURE__*/ React.createElement(Nav, {
        className: s['root'],
        id: id
    }, /*#__PURE__*/ React.createElement(VisuallyHidden, null, /*#__PURE__*/ React.createElement(Heading, null, t('common.networkBar'))), isMobile && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SiteHeaderPlacement, {
        className: s['dealer-ad']
    })), /*#__PURE__*/ React.createElement("ul", null, isMobile && !hasCoreMembership && /*#__PURE__*/ React.createElement("li", null, /*#__PURE__*/ React.createElement(NetworkBarLink, {
        label: "mobileCore",
        url: Url.coreMembershipLink(),
        icon: CoreIcon,
        tabIndex: tabIndex,
        // Doubling up so that we can change the color of the text and the icon
        linkClassName: s['core-item'],
        iconClassName: s['core-item']
    })), mainNavItemsFiltered.map((item)=>{
        var _item_pageType, _item_pageType1;
        return /*#__PURE__*/ React.createElement("li", {
            key: item.key
        }, /*#__PURE__*/ React.createElement(NetworkBarLink, {
            label: item.key,
            url: item.url,
            icon: menuItemIconMap[item.key],
            tabIndex: tabIndex,
            isCollapsed: !isMobile && isCollapsed,
            isSelected: selectedItem === ((_item_pageType = item.pageType) !== null && _item_pageType !== void 0 ? _item_pageType : item.key),
            className: classnames(selectedItem === ((_item_pageType1 = item.pageType) !== null && _item_pageType1 !== void 0 ? _item_pageType1 : item.key) && s['selected-item']),
            hasNewContent: item.hasNewContent,
            betaBadge: item.betaBadge,
            showTooltip: true,
            menuItems: isMobile && item.key === 'watch' && canSeeOldWatch ? [
                {
                    label: t('networkBar.navItems.watch.actions.oldWatch'),
                    link: Url.watchLink(),
                    biData: BiData({
                        evid: 163,
                        sectionname: item.key,
                        indicator: item.hasNewContent ? 1 : 0,
                        menu_item: 'oldWatch'
                    })
                }
            ] : undefined
        }));
    }), showWatchedGroupsEmptyState && isCollapsed && isLoggedIn && /*#__PURE__*/ React.createElement("li", null, createGroupLink)), showWatchedGroupsEmptyState && !isCollapsed && /*#__PURE__*/ React.createElement("div", {
        className: s['group']
    }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'groups'
        }
    }, /*#__PURE__*/ React.createElement(NetworkBarItemGroup, {
        title: t('networkBar.subheader.groups'),
        icon: AddToGroupFill,
        isCollapsed: isCollapsed
    }, isLoggedIn && createGroupLink, /*#__PURE__*/ React.createElement(EmptyWatchedGroups, null)))), !showWatchedGroupsEmptyState && isLoggedIn && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'groups'
        }
    }, /*#__PURE__*/ React.createElement(FoldableGroupList, {
        sectionType: "watched_groups",
        streamId: watchedGroupsStreamId,
        tabIndex: tabIndex,
        selectedItem: selectedItem,
        isCollapsed: isCollapsed,
        selectedItemClassName: s['selected-item'],
        icon: AddToGroupFill,
        title: t('networkBar.subheader.groups'),
        toggleAriaLabel: t('networkBar.subheader.groups'),
        prefix: createGroupLink,
        className: s['group']
    })), showRecommendedGroups && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'recommended groups'
        }
    }, /*#__PURE__*/ React.createElement(FoldableGroupList, {
        sectionType: "recommended_groups",
        streamId: recommendedGroupsStreamId,
        tabIndex: tabIndex,
        selectedItem: selectedItem,
        isCollapsed: isCollapsed,
        selectedItemClassName: s['selected-item'],
        className: s['group'],
        icon: RecommendedIcon,
        title: t('networkBar.subheader.recommendedGroups'),
        toggleAriaLabel: t('networkBar.subheader.recommendedGroups')
    })))));
};
NetworkBar.displayName = 'NetworkBar';
export default NetworkBar;

//# sourceMappingURL=NetworkBar.js.map